/**
 * May the signed-in person create a session, an item or an item pool here?
 *
 * Each answer mirrors the table's insert rule on the server, so a page draws a
 * create control only for someone the insert will accept (build contract §2.8,
 * form 6), and draws the matching line for everyone else:
 *
 * - **Session** — `prioritization_sessions_workspace_insert` (migration 754)
 *   takes the SESSION_RUN capability, which the client holds as
 *   `session.facilitate` or `session.record`.
 * - **Item** — `items_insert_with_permission` (migration 751) takes
 *   `items.create` (or ITEM_CRUD, which `can('items.create')` already counts).
 * - **Item pool** — `item_pools_insert_with_permission` (migration 754) takes
 *   POOL_SELECT for a pool with no cycle and no track, the kind every caller
 *   here creates; the client holds it as `lists.create`.
 *
 * The database stays the authority; these only decide what is drawn.
 */
import type { PermissionAction } from '@/types/prioritization/permissions';
import { useUserStore } from '@/stores/prioritization/userStore';

type Can = (action: PermissionAction) => boolean;

export function sessionCreateAllowed(can: Can): boolean {
  return can('session.facilitate') || can('session.record');
}

export function itemCreateAllowed(can: Can): boolean {
  return can('items.create');
}

export function poolCreateAllowed(can: Can): boolean {
  return can('lists.create');
}

export const SESSION_CREATE_LINE =
  'Starting a session takes the Facilitate Sessions or Record Decisions permission. A workspace admin can grant it.';

export const ITEM_CREATE_LINE =
  'Adding items takes the Create Items permission. A workspace admin can grant it.';

export const POOL_CREATE_LINE =
  'Saving this as an item pool takes the Create Item Pools permission. A workspace admin can grant it.';

/**
 * The ball line for someone without the right: it names who holds the ball,
 * so the line above the refusal never invites an act the person may not do
 * (build contract §2.8, "a name that promises an act").
 */
export const SESSION_CREATE_BALL =
  'someone with the Facilitate Sessions or Record Decisions permission — they start sessions';

export const ITEM_CREATE_BALL = 'someone with the Create Items permission — they add items';

export interface CreateRights {
  session: boolean;
  items: boolean;
  pool: boolean;
}

/** All three answers for the signed-in person, in the current workspace. */
export function useCreateRights(): CreateRights {
  const can = useUserStore((s) => s.can);
  // `can` is one stable function that reads the store through get(), so
  // subscribe to what it reads: the page re-renders when permissions arrive.
  useUserStore((s) => s.permissionsLoaded);
  useUserStore((s) => s.currentRole);
  useUserStore((s) => s.assignedRoleNames);
  return {
    session: sessionCreateAllowed(can),
    items: itemCreateAllowed(can),
    pool: poolCreateAllowed(can),
  };
}
